import React, { useState, useEffect, useCallback } from "react";
import toastr from "toastr";
import { authFetch } from "../utils/authFetch";

// Sample authFetch with JSON headers & token authorization
// async function authFetch(url, options = {}) {
//   const token = localStorage.getItem("token");
//   const headers = {
//     "Content-Type": "application/json",
//     ...(token ? { Authorization: `Bearer ${token}` } : {}),
//     ...options.headers,
//   };

//   const opts = { ...options, headers };

//   try {
//     const res = await fetch(url, opts);
//     return res;
//   } catch (error) {
//     console.error("Fetch error:", error);
//     return null;
//   }
// }

function CompanyProfile() {
  const [companyForm, setCompanyForm] = useState({
    companyname: "",
    shortname: "",
    website: "",
    logo: "",
    status: true,
  });
  const [companies, setCompanies] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const showToast = (msg, type = "success") => {
    toastr.options = {
      closeButton: true,
      progressBar: true,
      positionClass: "toast-top-right",
      timeOut: "3000",
    };
    toastr[type](msg);
  };

  // useCallback to avoid eslint warning
  const fetchCompanies = useCallback(async () => {
    try {
      // const res = await authFetch("http://127.0.0.1:6060/api/v1/company/list");
       const res = await authFetch("/api/v1/company/list");
      if (!res) throw new Error("No response");
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast("Failed to fetch companies", "error");
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreateOrUpdate = async () => {
    if (!companyForm.companyname.trim()) {
      showToast("Company name is required", "error");
      return;
    }
    setLoading(true);
    const url = editingId
      ? "/api/v1/company/update"
      : "/api/v1/company/create";
    const method = editingId ? "PUT" : "POST";

    try {
      const bodyData = editingId ? { ...companyForm, id: editingId } : { ...companyForm };
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(bodyData),
      });
      if (!res) throw new Error("No response");
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");

      showToast(editingId ? "Company updated" : "Company created");
      setCompanyForm({ companyname: "", shortname: "", website: "", logo: "", status: true });
      setEditingId(null);
      fetchCompanies();

      // Close modal manually if using bootstrap modal JS
      document.querySelector("#companyModal .btn-close")?.click();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (comp) => {
    setCompanyForm({
      companyname: comp.companyname,
      shortname: comp.shortname,
      website: comp.website,
      logo: comp.logo,
      status: comp.status,
    });
    setEditingId(comp.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    try {
      const res = await authFetch(`/api/v1/company/delete/${id}`, {
        method: "DELETE",
      });
      if (!res) throw new Error("No response");
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Delete failed");

      showToast("Company deleted", "success");
      fetchCompanies();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyForm({
      ...companyForm,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <div className="page-wrapper">
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="card">
            <div className="card-body">
              <div className="card-title d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Company Profile Manager</h4>
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#companyModal"
                  onClick={() => {
                    setCompanyForm({ companyname: "", shortname: "", website: "", logo: "", status: true });
                    setEditingId(null);
                  }}
                >
                  Add Company
                </button>
              </div>

              {/* Modal */}
              <div className="modal fade" id="companyModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{editingId ? "Edit Company" : "Add Company"}</h5>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                      <div className="mb-2">
                        <label htmlFor="companyname" className="form-label">Company Name</label>
                        <input
                          id="companyname"
                          name="companyname"
                          type="text"
                          className="form-control"
                          placeholder="Enter company name"
                          value={companyForm.companyname}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="mb-2">
                        <label htmlFor="shortname" className="form-label">Short Name</label>
                        <input
                          id="shortname"
                          name="shortname"
                          type="text"
                          className="form-control"
                          placeholder="Enter short name"
                          value={companyForm.shortname}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-2">
                        <label htmlFor="website" className="form-label">Website</label>
                        <input
                          id="website"
                          name="website"
                          type="url"
                          className="form-control"
                          placeholder="https://example.com"
                          value={companyForm.website}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-2">
                        <label htmlFor="logo" className="form-label">Company Logo (Base64)</label>
                        <input
                          id="logo"
                          name="logo"
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setCompanyForm({ ...companyForm, logo: reader.result });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </div>

                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          id="status"
                          name="status"
                          className="form-check-input"
                          checked={companyForm.status}
                          onChange={handleChange}
                        />
                        <label htmlFor="status" className="form-check-label">Active</label>
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button className="btn btn-primary" onClick={handleCreateOrUpdate} disabled={loading}>
                        {loading ? "Saving..." : editingId ? "Update" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <hr />
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Short Name</th>
                      <th>Website</th>
                      <th>Logo</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">No Companies found</td>
                      </tr>
                    )}
                    {companies.map((comp) => (
                      <tr key={comp.id}>
                        <td>{comp.companyname}</td>
                        <td>{comp.shortname}</td>
                        <td>{comp.website}</td>
                        <td>
                          {comp.logo ? (
                            <img src={comp.logo} alt="logo" style={{ width: "50px" }} />
                          ) : (
                            "No Logo"
                          )}
                        </td>
                        <td>{comp.status ? "Active" : "Inactive"}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-info me-2"
                            data-bs-toggle="modal"
                            data-bs-target="#companyModal"
                            onClick={() => handleEdit(comp)}
                          >
                            Edit
                          </button>
                          {/* <button className="btn btn-sm btn-danger" onClick={() => handleDelete(comp.id)}>
                            Delete
                          </button> */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyProfile;
